import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardBody, CardHeader, CardTitle, Col, Row, Button } from 'react-bootstrap';
import warehouseRepository from '../../../services/repositories/warehouse.repository';
import '../../../styles/products.scss';

const AddWarehouse = ({ isEdit = false, warehouseId = null }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    status: 'active',
    max_qte: '',
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    if (isEdit && warehouseId) {
      fetchWarehouseData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, warehouseId]);

  const fetchWarehouseData = async () => {
    try {
      setFetching(true);
      setError(null);
      
      const response = await warehouseRepository.getById(warehouseId);
      const warehouse = response.data || response;
      
      if (!warehouse || !warehouse.id) {
        throw new Error('Warehouse data not found');
      }
      
      setFormData({
        name: warehouse.name || '',
        status: warehouse.status || 'active',
        max_qte: warehouse.max_qte || '',
      });
    } catch (err) {
      console.error('Error fetching warehouse:', err);
      setError(err.message || 'Error loading warehouse data');
    } finally {
      setFetching(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const submitData = {
        name: formData.name || '',
        status: formData.status || 'active',
        max_qte: formData.max_qte ? parseInt(formData.max_qte) : null,
      };
      
      if (isEdit && warehouseId) {
        await warehouseRepository.update(warehouseId, submitData);
      } else {
        await warehouseRepository.create(submitData);
      }
      
      navigate('/admin/warehouses/warehouse-list');
    } catch (err) {
      console.error('Error saving warehouse:', err);
      setError(err.response?.data?.message || err.message || 'Erreur lors de la sauvegarde de l\'entrepôt');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/warehouses/warehouse-list');
  };

  if (fetching) {
    return (
      <Col xl={12} lg={12}>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="mt-3">Chargement des données de l'entrepôt...</p>
        </div>
      </Col>
    );
  }

  return (
    <Col xl={12} lg={12}>
      {error && (
        <div className="alert alert-danger mb-3" role="alert">
          {error}
        </div>
      )}
      
      <Card>
        <CardHeader>
          <CardTitle as={'h6'} className="mb-0" style={{ fontSize: '16px', fontWeight: '500' }}>Informations de l'Entrepôt</CardTitle>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleSubmit}>
            <Row>
              <Col lg={6}>
                <div className="mb-3">
                  <label htmlFor="warehouse-name" className="form-label">
                    Nom de l'Entrepôt <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    id="warehouse-name"
                    name="name"
                    className="form-control"
                    placeholder="Nom de l'entrepôt"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </Col>
              <Col lg={6}>
                <div className="mb-3">
                  <label htmlFor="warehouse-status" className="form-label">
                    Statut
                  </label>
                  <select
                    className="form-control"
                    id="warehouse-status"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                  >
                    <option value="active">Actif</option>
                    <option value="inactive">Inactif</option>
                  </select>
                </div>
              </Col>
            </Row>
            <Row>
              <Col lg={6}>
                <div className="mb-3">
                  <label htmlFor="warehouse-max-qte" className="form-label">
                    Quantité Maximale
                  </label>
                  <input
                    type="number"
                    id="warehouse-max-qte"
                    name="max_qte"
                    className="form-control"
                    placeholder="Quantité maximale"
                    value={formData.max_qte}
                    onChange={handleInputChange}
                    min="0"
                  />
                </div>
              </Col>
            </Row>
            
            <div className="p-3 bg-light mb-3 rounded">
              <Row className="justify-content-end g-2">
                <Col lg={2}>
                  <Button 
                    variant="dark" 
                    className="w-100" 
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Annuler
                  </Button>
                </Col>
                <Col lg={2}>
                  <Button 
                    variant="primary" 
                    className="w-100" 
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? 'Enregistrement...' : (isEdit ? 'Enregistrer' : 'Créer l\'Entrepôt')}
                  </Button>
                </Col>
              </Row>
            </div>
          </form>
        </CardBody>
      </Card>
    </Col>
  );
};

export default AddWarehouse;
